import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Brain, Zap, BarChart, ChevronRight, Play, Star, Shield, Cpu, Database, Cloud, Bot, Building, ShoppingCart, Heart, Truck, Car, Plane, GraduationCap, Gamepad2, Coffee, Code, FileText, Layout, Bug, TestTube, MessageSquare, Users, Target, Lightbulb, Cog, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/assets/images";
import { ROUTE_PATHS, cn } from "@/lib/index";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

const Agents = () => {
  return (
    <div className="flex flex-col w-full overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-10">
          <img 
            src={IMAGES.TECH_HERO_BG} 
            alt="AI Agents Background"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-20">
          <div className="max-w-5xl">
            {/* Badge */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={springPresets.gentle}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/30 to-yellow-400/20 border border-primary/40 text-primary text-sm font-medium mb-8 backdrop-blur-sm"
            >
              <Bot size={16} className="animate-pulse" />
              <span>Introduciendo</span>
            </motion.div>
            
            {/* Main Headline */}
            <motion.h1 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.smooth, delay: 0.1 }}
              className="text-5xl md:text-7xl font-bold tracking-tight mb-8 text-white leading-tight"
            >
              Software independiente e inteligente diseñado para{' '}
              <span className="bg-gradient-to-r from-primary via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                transformar las operaciones empresariales
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.smooth, delay: 0.2 }}
              className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl leading-relaxed"
            >
              Potencia tu Ciclo de Vida de Desarrollo de Software con Pryma CODA a través de Agentes de IA, asistentes y herramientas diseñadas para amplificar el rendimiento humano y acelerar la entrega.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.smooth, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link 
                to={ROUTE_PATHS.CONTACT} 
                className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold text-lg rounded-xl hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-primary/30"
              >
                Conectemos
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <button className="group inline-flex items-center gap-3 px-10 py-5 border-2 border-primary text-primary font-bold text-lg rounded-xl hover:bg-primary hover:text-black transition-all duration-300">
                Saber más
                <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-black">
              Descubre cómo los Agentes de IA están transformando industrias con{' '}
              <span className="bg-gradient-to-r from-primary to-yellow-500 bg-clip-text text-transparent">
                aumentos de eficiencia notables
              </span>
            </h2>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                number: "85",
                suffix: "%",
                title: "Aumento en Eficiencia y Ahorro de Costos",
                desc: "Optimiza operaciones mientras reduces costos operacionales."
              },
              {
                number: "92",
                suffix: "%",
                title: "Experiencia del Cliente Mejorada",
                desc: "Impulsa la satisfacción a través de interacciones inteligentes de IA."
              },
              {
                number: "60",
                suffix: "%",
                title: "Tiempo de Comercialización Acelerado",
                desc: "Lleva innovaciones al mercado más rápido que nunca."
              }
            ].map((stat, idx) => (
              <motion.div 
                key={idx}
                variants={staggerItem}
                className="text-center p-8 rounded-3xl bg-white border border-gray-200 hover:border-primary/30 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10"
              >
                <div className="text-6xl md:text-7xl font-black mb-4">
                  <span className="bg-gradient-to-r from-primary to-yellow-500 bg-clip-text text-transparent">
                    {stat.number}
                  </span>
                  <span className="text-gray-400">{stat.suffix}</span>
                </div>
                <h3 className="text-xl font-bold mb-3 text-black">{stat.title}</h3>
                <p className="text-gray-600 leading-relaxed">{stat.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-gray-600 mb-4">Impulsado por una encuesta global de</p>
            <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors">
              Leer el Informe Completo
              <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* AI Agents Power Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              El poder de los{' '}
              <span className="bg-gradient-to-r from-primary to-yellow-400 bg-clip-text text-transparent">
                Agentes de IA de Pryma
              </span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-xl text-gray-300 max-w-4xl mx-auto"
            >
              Amplifican la experiencia humana para transformar la productividad e impulsar ganancias significativas a escala.
            </motion.p>
          </div>

          {/* Video/Demo Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="relative max-w-4xl mx-auto mb-16"
          >
            <div className="relative aspect-video rounded-3xl overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700">
              <img 
                src={IMAGES.ANALYTICS_1} 
                alt="AI Agents Demo"
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <button className="w-20 h-20 bg-gradient-to-r from-primary to-yellow-400 rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl">
                  <Play className="w-8 h-8 text-black ml-1" />
                </button>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-2xl font-bold mb-2">La Revolución de la IA Generativa</h3>
                <p className="text-gray-300">Aprende cómo nuestro Agente de IA logró el mejor rendimiento en SWE-Bench-Lite y qué significa para la codificación.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Agentic Suite for Software Development */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold mb-6 text-black"
            >
              Nuestra suite agéntica para{' '}
              <span className="bg-gradient-to-r from-primary to-yellow-500 bg-clip-text text-transparent">
                Desarrollo de Software
              </span>
            </motion.h2>
          </div>

          {/* Software Development Agents Grid */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
          >
            {[
              {
                title: 'Product Definition AI',
                desc: 'Convierte insights de reuniones en datos accionables. Analiza grabaciones para crear resúmenes completos, modelos de datos robustos e historias de usuario, asegurando integración con Jira y alineación con requisitos específicos.',
                icon: FileText,
                bgClass: 'bg-gradient-to-br from-blue-50 to-blue-100',
                iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600'
              },
              {
                title: 'Backend Prototyping AI',
                desc: 'Crea sin esfuerzo backends de aplicaciones empresariales. A partir de una descripción detallada, genera un backend completamente personalizable, listo para web y móvil.',
                icon: Database,
                bgClass: 'bg-gradient-to-br from-green-50 to-green-100',
                iconBg: 'bg-gradient-to-br from-green-500 to-green-600'
              },
              {
                title: 'Application Design AI',
                desc: 'Transforma texto en diseños de aplicación dinámicos. Describe las características deseadas y obtén un diseño funcional que puede ser ajustado para cumplir con tus requisitos.',
                icon: Layout,
                bgClass: 'bg-gradient-to-br from-purple-50 to-purple-100',
                iconBg: 'bg-gradient-to-br from-purple-500 to-purple-600'
              },
              {
                title: 'Code Fixer AI',
                desc: 'Optimiza la resolución de errores en aplicaciones existentes. Analiza problemas reportados, crea un plan de resolución y despliega la solución una vez aprobada, asegurando resultados rápidos y precisos.',
                icon: Bug,
                bgClass: 'bg-gradient-to-br from-red-50 to-red-100',
                iconBg: 'bg-gradient-to-br from-red-500 to-red-600'
              },
              {
                title: 'Test Agentic Capabilities',
                desc: 'Lleva la IA al núcleo de tus estrategias de testing. Identifica bugs y asegura la calidad del software con testing de alta velocidad y precisión para entregas más rápidas.',
                icon: TestTube,
                bgClass: 'bg-gradient-to-br from-orange-50 to-orange-100',
                iconBg: 'bg-gradient-to-br from-orange-500 to-orange-600'
              },
              {
                title: 'DevOps Automation AI',
                desc: 'Automatiza pipelines de CI/CD, gestión de infraestructura y monitoreo de aplicaciones con agentes inteligentes que optimizan el rendimiento y reducen el tiempo de inactividad.',
                icon: Cog,
                bgClass: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
                iconBg: 'bg-gradient-to-br from-yellow-500 to-yellow-600'
              }
            ].map((agent, idx) => (
              <motion.div 
                key={idx}
                variants={staggerItem}
                className={`group p-8 rounded-3xl border border-gray-200 hover:border-primary/30 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-1 ${agent.bgClass}`}
              >
                <div className={`w-16 h-16 rounded-2xl ${agent.iconBg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <agent.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-black group-hover:text-primary transition-colors">
                  {agent.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {agent.desc}
                </p>
                <ChevronRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Industry Specialized Agents */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Potencia tu industria con{' '}
              <span className="bg-gradient-to-r from-primary to-yellow-400 bg-clip-text text-transparent">
                Agentes de IA Especializados
              </span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-xl text-gray-300 max-w-4xl mx-auto"
            >
              Empoderamos a las organizaciones para transformar no solo sus estrategias, sino también sus procesos y experiencias de usuario, impulsando innovación y creatividad.
            </motion.p>
          </div>

          {/* Featured Industry Agents */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16"
          >
            {[
              {
                title: 'Retail AI Agent',
                subtitle: 'Optimiza tu experiencia y gestión de retail',
                desc: 'Transforma la experiencia del cliente con personalización impulsada por IA, gestión inteligente de inventario y análisis predictivo de demanda.',
                image: IMAGES.AUTOMATION_6,
                features: ['Personalización en tiempo real', 'Gestión predictiva de inventario', 'Análisis de comportamiento del cliente', 'Optimización de precios dinámicos'],
                cta: 'Optimizar Retail'
              },
              {
                title: 'Bank Modernization AI Agent',
                subtitle: 'Transiciona tu código legacy más rápido',
                desc: 'Acelera la modernización bancaria con migración automatizada de sistemas legacy, cumplimiento regulatorio y gestión de riesgos inteligente.',
                image: IMAGES.ANALYTICS_4,
                features: ['Migración automatizada de legacy', 'Cumplimiento regulatorio automático', 'Gestión inteligente de riesgos', 'Detección de fraudes en tiempo real'],
                cta: 'Modernizar Banca'
              }
            ].map((agent, idx) => (
              <motion.div 
                key={idx}
                variants={staggerItem}
                className="group bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 hover:border-primary/50 transition-all duration-300 backdrop-blur-sm rounded-3xl overflow-hidden"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={agent.image} 
                    alt={agent.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl font-bold mb-2 text-primary">{agent.title}</h3>
                    <p className="text-gray-300 text-sm">{agent.subtitle}</p>
                  </div>
                </div>
                <div className="p-8">
                  <p className="text-gray-300 leading-relaxed mb-6">{agent.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {agent.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-center gap-2 text-sm text-gray-400">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button className="w-full px-6 py-3 bg-gradient-to-r from-primary to-yellow-400 text-black font-bold rounded-xl hover:scale-105 transition-all duration-300">
                    {agent.cta}
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* All Industries Grid */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl font-bold mb-8">Agentes especializados para todas las industrias</h3>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6"
          >
            {[
              { name: 'Banca', icon: Building },
              { name: 'Retail', icon: ShoppingCart },
              { name: 'Salud', icon: Heart },
              { name: 'Logística', icon: Truck },
              { name: 'Seguros', icon: Shield },
              { name: 'Telecomunicaciones', icon: Cpu },
              { name: 'Manufactura', icon: Cog },
              { name: 'Energía', icon: Zap },
              { name: 'Educación', icon: GraduationCap },
              { name: 'Gaming', icon: Gamepad2 }
            ].map((industry, idx) => (
              <motion.div 
                key={idx}
                variants={staggerItem}
                className="group p-6 rounded-2xl bg-gray-800/30 border border-gray-700/30 hover:border-primary/50 transition-all duration-300 text-center hover:bg-gradient-to-br hover:from-primary/10 hover:to-yellow-400/10"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-primary/20 to-yellow-400/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <industry.icon className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-semibold text-white group-hover:text-primary transition-colors">{industry.name}</h4>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Enterprise AI Platform */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold mb-8 text-black"
            >
              Construye tu propio{' '}
              <span className="bg-gradient-to-r from-primary to-yellow-500 bg-clip-text text-transparent">
                Agente de IA
              </span>{' '}
              y remodela tu negocio
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 mb-12 leading-relaxed"
            >
              Pryma Enterprise AI es nuestra Plataforma Aceleradora de IA que te permite crear fácilmente Agentes de IA y Asistentes adaptados a las necesidades de tu negocio sin codificación. Simplifica la adopción de IA generativa dentro de un entorno de nivel empresarial al abordar barreras como la seguridad de datos y el acceso a modelos.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link 
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold text-lg rounded-xl hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-primary/30"
              >
                Haz que suceda
                <ArrowRight className="w-5 h-5" />
              </Link>
              <button className="inline-flex items-center gap-3 px-10 py-5 border-2 border-primary text-primary font-bold text-lg rounded-xl hover:bg-primary hover:text-black transition-all duration-300">
                Lleva tu negocio al siguiente nivel
                <TrendingUp className="w-5 h-5" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-black via-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={IMAGES.INDUSTRIES_GRID} 
            alt="Background Pattern"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-5xl md:text-7xl font-bold mb-8">
              Sé parte de la{' '}
              <span className="bg-gradient-to-r from-primary via-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                revolución de la IA
              </span>{' '}
              ahora
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Me gustaría suscribirme con mi dirección de correo electrónico para recibir comunicaciones de Pryma Digital con actualizaciones, recursos valiosos y consejos útiles.
            </p>
            
            {/* Newsletter Form */}
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto mb-8">
              <input 
                type="email" 
                placeholder="Tu email corporativo"
                className="flex-1 px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-primary backdrop-blur-sm"
              />
              <button className="px-8 py-4 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold rounded-xl hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-primary/30">
                Enviar
              </button>
            </div>
            
            <p className="text-sm text-gray-400 mb-12">
              Al enviar este formulario confirmas que estás de acuerdo con la política de privacidad de Pryma Digital.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Agents;